import { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartPieIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../utils/axios';

// Category colors
const CATEGORY_COLORS = {
  employee: '#3B82F6',
  security: '#EF4444',
  api: '#8B5CF6',
  device: '#10B981',
  ws: '#6366F1',
  nginx: '#F59E0B',
  ssh: '#EC4899',
  system: '#6B7280',
  unknown: '#9CA3AF',
};

// Action colors (rotating)
const ACTION_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

// Custom tooltip for pie chart
function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const data = payload[0];
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-2 text-sm">
      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: data.payload.fill }}
        />
        <span className="font-medium text-gray-900">{data.name}</span>
      </div>
      <p className="text-gray-600 mt-1">
        {data.value} events ({data.payload.percentage}%)
      </p>
    </div>
  );
}

// Breakdown card component
function BreakdownCard({ title, data, colorMap, colors }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
        <p className="text-sm text-gray-400">No data</p>
      </div>
    );
  }

  // Prepare chart data
  const chartData = data.slice(0, 6).map((item, idx) => ({
    name: item.category || item.action || item.outcome || item.level || 'unknown',
    value: item.count,
    percentage: item.percentage,
    fill: colorMap
      ? colorMap[item.category || item.action || item.outcome || item.level] || colors[idx % colors.length]
      : colors[idx % colors.length],
  }));

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={55}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="mt-2 space-y-1">
        {chartData.slice(0, 4).map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: item.fill }}
              />
              <span className="text-gray-600 truncate max-w-[80px]">{item.name}</span>
            </div>
            <span className="text-gray-500">{item.percentage}%</span>
          </div>
        ))}
        {chartData.length > 4 && (
          <p className="text-xs text-gray-400 text-center">
            +{chartData.length - 4} more
          </p>
        )}
      </div>
    </div>
  );
}

// Outcome bar component
function OutcomeBar({ data }) {
  if (!data || data.length === 0) return null;

  const success = data.find(d => d.outcome === 'success')?.count || 0;
  const failure = data.find(d => d.outcome === 'failure')?.count || 0;
  const total = success + failure;

  if (total === 0) return null;

  const successPct = Math.round((success / total) * 100);
  const failurePct = 100 - successPct;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Outcome</h4>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-green-500"
            style={{ width: `${successPct}%` }}
          />
          <div
            className="h-full bg-red-500"
            style={{ width: `${failurePct}%` }}
          />
        </div>
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-green-600">Success: {success} ({successPct}%)</span>
        <span className="text-red-600">Failure: {failure} ({failurePct}%)</span>
      </div>
    </div>
  );
}

// Level breakdown
function LevelBreakdown({ data }) {
  if (!data || data.length === 0) return null;

  const levelColors = {
    info: 'bg-green-500',
    warn: 'bg-yellow-500',
    error: 'bg-red-500',
    audit: 'bg-blue-500',
    debug: 'bg-gray-400',
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-3">By Level</h4>
      <div className="space-y-2">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${levelColors[item.level] || 'bg-gray-400'}`} />
            <span className="text-xs text-gray-600 flex-1">{item.level}</span>
            <span className="text-xs text-gray-500">{item.count}</span>
            <span className="text-xs text-gray-400">{item.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ActivityBreakdown({ entityType, entityId, timeRange = '7d' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBreakdown();
  }, [entityType, entityId, timeRange]);

  const fetchBreakdown = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(
        `/logs/activity/breakdown/${entityType}/${encodeURIComponent(entityId)}`,
        { params: { timeRange } }
      );

      setData(response.data.data);
    } catch (err) {
      console.error('Error fetching breakdown:', err);
      setError(err.response?.data?.error?.message || 'Failed to load breakdown');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ChartPieIcon className="w-5 h-5 text-gray-400" />
          <h3 className="font-medium text-gray-900">Activity Breakdown</h3>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ChartPieIcon className="w-5 h-5 text-gray-400" />
            <h3 className="font-medium text-gray-900">Activity Breakdown</h3>
          </div>
          <button onClick={fetchBreakdown} className="text-blue-600 hover:text-blue-700 text-sm">
            Retry
          </button>
        </div>
        <div className="h-48 flex items-center justify-center text-gray-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ChartPieIcon className="w-5 h-5 text-purple-500" />
          <h3 className="font-medium text-gray-900">Activity Breakdown</h3>
        </div>
        <button
          onClick={fetchBreakdown}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* By Category */}
        <BreakdownCard
          title="By Category"
          data={data?.byCategory}
          colorMap={CATEGORY_COLORS}
          colors={ACTION_COLORS}
        />

        {/* By Action */}
        <BreakdownCard
          title="Top Actions"
          data={data?.byAction}
          colors={ACTION_COLORS}
        />

        {/* Outcome */}
        <OutcomeBar data={data?.byOutcome} />

        {/* By Level */}
        <LevelBreakdown data={data?.byLevel} />
      </div>
    </div>
  );
}
